import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import noop from 'lodash/noop';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text, TextType } from 'ui-components/components/text';

import { rateShopperMessages, commonMessages } from './messages';
import style from './rate-shopper-promo-recommendation-banner.module.less';
import { useRateShopperPromoDismissal } from './use-rate-shopper-promo-dismissal';
import { TRACKING } from '../../fulfillment-planner/rate-recommendation/constants';
import { Segment } from '../../services/analytics-services/segment';
import Global from '../../store/global-selectors';
import { type RootState } from '../../store/root-state';
import { type SellerLabelProvider, type SellerService } from '../../types/seller-data';
import { cswAddRateShopperServiceAndRerate } from '../../store/csw/csw.actions';
import { FormattedCurrency } from '../formatted-currency';

import { RateRecommendationBanner } from '../../fulfillment-planner/rate-recommendation/recommendation/rate-recommendation-banner';

type RateShopperPromoRecommendation = {
  shippingServiceId: string;
  sellerProviderId: string;
  confirmationId: string;
  packageTypeId: string;
  currencyCode: string;
  savings: number;
};

const trackEvent = (eventName: string, params: Record<string, unknown>, sellerMetadata: Record<string, unknown>) => {
  Segment.trackEvent(eventName, { ...params, ...sellerMetadata });
};

const makeShippingServiceSelector = () =>
  createSelector(
    (promoRate: RateShopperPromoRecommendation) => promoRate,
    (_: RateShopperPromoRecommendation, serviceCollectionGuid: string) => serviceCollectionGuid,
    (_: RateShopperPromoRecommendation, __: string, state: RootState) => Global.selectSellerServices(state),
    (_: RateShopperPromoRecommendation, __: string, state: RootState) => Global.selectSellerLabelProviders(state),
    (
      promoRate: RateShopperPromoRecommendation,
      serviceCollectionGuid: string,
      shippingServices: SellerService[],
      sellerLabelProviders: SellerLabelProvider[],
    ) => {
      if (!promoRate?.shippingServiceId) {
        return {};
      }

      const service = shippingServices.find((s) => {
        return s.serviceId === promoRate.shippingServiceId && s.sellerLabelProviderId === promoRate.sellerProviderId && !s.isHidden;
      });
      if (!service) {
        return {};
      }

      const allMatchingProviders = sellerLabelProviders.filter((s) => s.labelProviderId === promoRate.sellerProviderId);
      const primaryProvider = allMatchingProviders.find((p) => p.isPrimaryAccount) ?? allMatchingProviders[0];

      return {
        serviceToAddToServiceCollection: {
          carrierId: service.carrierId,
          confirmationId: promoRate.confirmationId,
          packageTypeId: promoRate.packageTypeId,
          providerId: service.labelProviderId,
          sellerProviderId: service.sellerLabelProviderId,
          sellerProviderName: primaryProvider?.name,
          serviceCollectionGuid,
          shippingServiceId: service.serviceId,
          shippingServiceName: service.name,
        },
      };
    },
  );

type RateShopperPromoRecommendationBannerProps = {
  fulfillmentPlanId: string;
  onShippingServiceAdded?: () => void;
  promoRate: RateShopperPromoRecommendation;
  serviceCollectionGuid: string;
};

export const RateShopperPromoRecommendationBanner: React.FC<RateShopperPromoRecommendationBannerProps> = ({
  serviceCollectionGuid,
  fulfillmentPlanId,
  promoRate,
  onShippingServiceAdded = noop,
}) => {
  const dispatch = useDispatch();
  const [isDismissed, dismiss] = useRateShopperPromoDismissal(fulfillmentPlanId, serviceCollectionGuid);

  const sellerId = useSelector(Global.selectSellerSellerId);

  const shippingServiceSelector = useMemo(makeShippingServiceSelector, []);
  const { serviceToAddToServiceCollection } = useSelector((state) => shippingServiceSelector(promoRate, serviceCollectionGuid, state));

  const analyticsData = useMemo(() => {
    return {
      fulfillmentPlanId,
      serviceCollectionGuid,
      serviceId: promoRate?.shippingServiceId,
    };
  }, [fulfillmentPlanId, serviceCollectionGuid, promoRate?.shippingServiceId]);

  const onAddToCollection = useCallback(() => {
    if (serviceToAddToServiceCollection) {
      onShippingServiceAdded();
      dispatch(cswAddRateShopperServiceAndRerate(fulfillmentPlanId, serviceCollectionGuid, serviceToAddToServiceCollection));
      trackEvent(TRACKING.RATE_SHOPPER_PROMO_APPLIED, analyticsData, { sellerId });
    }
  }, [
    dispatch,
    serviceToAddToServiceCollection,
    fulfillmentPlanId,
    serviceCollectionGuid,
    analyticsData,
    sellerId,
    onShippingServiceAdded,
  ]);

  const onDismiss = useCallback(() => {
    dismiss();
    trackEvent(TRACKING.RATE_SHOPPER_PROMO_DISMISSED, analyticsData, { sellerId });
  }, [analyticsData, sellerId, dismiss]);

  if (isDismissed || !serviceToAddToServiceCollection) {
    return null;
  }

  return (
    <RateRecommendationBanner
      applyButton={
        <Button className={style['apply-button']} onClick={onAddToCollection} theme={THEME_CONSTANTS.white}>
          <FormattedMessage {...commonMessages.addButton} />
        </Button>
      }
      bannerText={
        <Text
          formattedMessageValues={{
            savings: () => {
              return (
                <b>
                  <FormattedCurrency currencyCode={promoRate.currencyCode} value={promoRate.savings} />
                </b>
              );
            },
            shippingServiceName: () => {
              return <b>{serviceToAddToServiceCollection.shippingServiceName}</b>;
            },
          }}
          textType={TextType.Paragraph}
          value={rateShopperMessages.savingsBanner}
        />
      }
      contentClassName={style['rate-shopper-savings-content']}
      onDismiss={onDismiss}
      theme='light'
    />
  );
};
